import { makeAutoObservable, runInAction } from 'mobx';
import { fetchAuthSession, signIn, signOut, getCurrentUser, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import type { User } from '../types';

class AuthStore {
  user: User | null = null;
  isAuthenticated = false;
  isLoading = true;
  error: string | null = null;
  impersonatingTenantId: string | null = null;
  impersonatingTenantName: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  async checkAuth() {
    this.isLoading = true;
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.idToken) {
        const user = await getCurrentUser();
        const idToken = session.tokens.idToken;
        const payload = idToken.payload;

        runInAction(() => {
          this.user = {
            id: user.userId,
            email: payload.email as string,
            name: payload.name as string || '',
            role: (payload['custom:role'] as 'root' | 'admin' | 'user') || 'user',
            tenantId: payload['custom:tenantId'] as string,
            createdAt: new Date().toISOString(),
          };
          this.isAuthenticated = true;
          this.error = null;
        });

        // sessionStorageからなりすまし状態を復元
        const storedImpersonation = sessionStorage.getItem('impersonation');
        if (storedImpersonation) {
          try {
            const { tenantId, tenantName } = JSON.parse(storedImpersonation);
            if (tenantId && tenantName) {
              runInAction(() => {
                this.impersonatingTenantId = tenantId;
                this.impersonatingTenantName = tenantName;
              });
            }
          } catch (e) {
            // 無効なデータの場合は削除
            sessionStorage.removeItem('impersonation');
          }
        }
      }
    } catch (error) {
      runInAction(() => {
        this.user = null;
        this.isAuthenticated = false;
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  async login(email: string, password: string) {
    this.isLoading = true;
    this.error = null;
    
    try {
      const { isSignedIn } = await signIn({ username: email, password });
      
      if (isSignedIn) {
        await this.checkAuth();
        return true;
      }
      return false;
    } catch (error: any) {
      // Handle "There is already a signed in user" error
      if (error.message?.includes('already a signed in user')) {
        // Sign out the existing user first
        await signOut();
        // Retry login
        try {
          const { isSignedIn } = await signIn({ username: email, password });
          if (isSignedIn) {
            await this.checkAuth();
            return true;
          }
        } catch (retryError: any) {
          runInAction(() => {
            this.error = retryError.message || 'ログインに失敗しました';
            this.isLoading = false;
          });
          return false;
        }
      }
      
      runInAction(() => {
        this.error = error.message || 'ログインに失敗しました';
        this.isLoading = false;
      });
      return false;
    }
  }

  async logout() {
    try {
      await signOut();
      runInAction(() => {
        this.user = null;
        this.isAuthenticated = false;
        this.error = null;
        this.impersonatingTenantId = null;
        this.impersonatingTenantName = null;
      });
      // ログアウト時にsessionStorageもクリア
      sessionStorage.removeItem('impersonation');
    } catch (error: any) {
      console.error('Logout error:', error);
    }
  }

  async requestPasswordReset(email: string) {
    this.isLoading = true;
    this.error = null;

    try {
      const output = await resetPassword({ username: email });
      runInAction(() => {
        this.isLoading = false;
      });
      return output;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'パスワードリセットのリクエストに失敗しました';
        this.isLoading = false;
      });
      throw error;
    }
  }

  async confirmPasswordReset(email: string, code: string, newPassword: string) {
    this.isLoading = true;
    this.error = null;

    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword: newPassword,
      });
      runInAction(() => {
        this.isLoading = false;
        this.error = null;
      });
      return true;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || 'パスワードリセットに失敗しました';
        this.isLoading = false;
      });
      return false;
    }
  }

  async getIdToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Failed to get ID token:', error);
      return null;
    }
  }

  get isRootAdmin() {
    return this.user?.role === 'root';
  }

  get isTenantAdmin() {
    return this.user?.role === 'admin';
  }

  get isTenantUser() {
    return this.user?.role === 'user';
  }

  get tenantId() {
    return this.user?.tenantId;
  }

  get effectiveTenantId() {
    return this.impersonatingTenantId || this.user?.tenantId;
  }

  get isImpersonating() {
    return this.impersonatingTenantId !== null;
  }

  startImpersonation(tenantId: string, tenantName: string) {
    runInAction(() => {
      this.impersonatingTenantId = tenantId;
      this.impersonatingTenantName = tenantName;
    });
    // sessionStorageに保存
    sessionStorage.setItem('impersonation', JSON.stringify({ tenantId, tenantName }));
  }

  stopImpersonation() {
    runInAction(() => {
      this.impersonatingTenantId = null;
      this.impersonatingTenantName = null;
    });
    // sessionStorageから削除
    sessionStorage.removeItem('impersonation');
  }
}

export const authStore = new AuthStore();